import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import AuthShell from './AuthShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AttendeeLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/users/login`, { email, password });
      localStorage.setItem('attendee_token', res.data.token);
      localStorage.setItem('attendee_id', res.data.attendee_id || '');
      if (res.data.attendee_id) {
        navigate(`/portal/${res.data.attendee_id}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Attendee Portal"
      title="Welcome back"
      subtitle="Sign in to view your retreat details."
      footer={
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-1.5 text-xs text-white/40 hover:text-white/80 transition-colors"
          data-testid="attendee-login-back"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to site
        </Link>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl p-7"
        data-testid="attendee-login-form"
      >
        <div className="flex items-center gap-2 mb-5">
          <Lock className="w-3.5 h-3.5 text-white/40" />
          <p className="eyebrow text-white/40">Sign In</p>
        </div>

        <div className="space-y-3">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 w-4 h-4" />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="pl-10 bg-white/[0.04] border-white/15 text-white placeholder:text-white/30 h-12 text-base focus-visible:ring-red-400/40"
              data-testid="login-email"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 w-4 h-4" />
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              required
              className="pl-10 pr-10 bg-white/[0.04] border-white/15 text-white placeholder:text-white/30 h-12 text-base focus-visible:ring-red-400/40"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
              aria-label="Toggle password visibility"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <p className="text-xs text-red-300 pl-1" data-testid="login-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white font-semibold text-base btn-lift group"
            data-testid="login-submit-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
            {!loading && (
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            )}
          </Button>

          <p className="text-center text-xs text-white/40 pt-1 leading-relaxed">
            <Link
              to="/forgot-password"
              className="text-white/70 hover:text-white underline underline-offset-4 transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </Link>
            {' '}· Need help? Email{' '}
            <a
              href="mailto:info@rojoretreat.com"
              className="text-white/70 hover:text-white underline underline-offset-4 transition-colors"
              data-testid="login-contact-link"
            >
              info@rojoretreat.com
            </a>
          </p>
        </div>
      </form>
    </AuthShell>
  );
};

export default AttendeeLogin;
